import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import Input from "../../../../../../components/Form/Input/input";
import { TextArea } from "../../../../../../components/Form/Input/textArea";
import Label from "../../../../../../components/Form/Label";
import { Form, FormGroup } from "../../../../../../components/Form/styles";
import { Box } from "../../../../../../components/Layout";
import SaveBar from "../../../../../../components/SaveBar";
import { SubHeader } from "../../../../../../components/SubHeader";
import { FaqItem } from "../../../../../../model/site";
import { clone, deepEqual } from "../../../../../../utils/objects";
import { uuidv4 } from "../../../../../../utils/uuid";
import { useItemPreview } from "../../../../hooks/Section";
import { navigationState } from "../../../Sections/navigationState";
import { FaqNavigationState } from "../../faqNavigationState";
import { Container } from "./styles";

const ItemFaqPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const faqNavigationState = navigationState.get() as FaqNavigationState;

  const headerTitle = useRef(
    faqNavigationState.editingFaq?.question
      ? t("pages.editor.faq.item.header.title.edit")
      : t("pages.editor.faq.item.header.title.add")
  );

  const initialItemState = useRef<FaqItem>(
    clone<FaqItem>(
      faqNavigationState.editingFaq || {
        id: uuidv4(),
        question: "",
        answer: "",
      }
    )
  );

  const [item, setItem] = useState(initialItemState.current);

  useItemPreview(
    faqNavigationState.currentSection,
    item,
    existing => existing.question === initialItemState.current.question
  );

  const isSaveDisabled = !item.question || !item.answer;

  const hasUnsavedChanges = () => !deepEqual(initialItemState.current, item);

  const saveHandler = () => {
    const faqs: FaqItem[] = faqNavigationState.currentSection.items || [];
    const itemIndex = faqs.findIndex(faq => faq.question === initialItemState.current.question);

    if (itemIndex >= 0) {
      faqs[itemIndex] = item;
    } else {
      faqs.push(item);
    }

    faqNavigationState.currentSection.items = faqs;
    navigate(-1);
  };

  return (
    <Container>
      <SubHeader
        title={headerTitle.current}
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.faq.header.title"), to: "/editor/faq" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Box>
        <Form>
          <FormGroup>
            <Input
              id="question"
              required
              type="text"
              maxLength={100}
              value={item.question}
              autoCapitalize={"on"}
              onChange={e => setItem({ ...item, question: e.target.value })}
              label={t("pages.editor.faq.item.form.question")}
            />
          </FormGroup>
          <FormGroup>
            <TextArea
              id="title"
              style={{ height: "8rem" }}
              required
              maxLength={200}
              autoCapitalize={"on"}
              value={item.answer}
              onChange={e => setItem({ ...item, answer: e.target.value })}
            />
            <Label
              htmlFor="title"
              maxLength={200}
              usedLength={item.answer.length}
              required
              text={t("pages.editor.faq.item.form.answer")}
            />
          </FormGroup>
        </Form>
      </Box>
      <SaveBar onSave={saveHandler} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
    </Container>
  );
};

export default ItemFaqPage;
